import * as React from 'react';
import { useEffect } from 'react';
import {
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Input,
  RadioGroup,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { listCatalogs } from 'src/api/catalog';
import { FOUNDRY_TRUSTED_ROLES, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { getFoundryRoleARN } from 'src/commons/common';
import { getRegion } from 'src/api/config';

export interface CatalogDetailsProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  catalogLoading: any;
  setCatalogLoading: any;
  dataSource: any;
  bucketName: any;
  CTI: any;
  catalogName: any;
  description: any;
  setDataSource: any;
  setBucketName: any;
  setCTI: any;
  setCatalogName: any;
  setDescription: any;
  setGetCatalogSuccess: any;
  getCatalogSuccess: boolean;
  setIsDefaultCatalog: any;
  isDefaultCatalog: boolean;
  setIsGroupBasedCatalog: any;
  isGroupBasedCatalog: boolean;
}

export const CatalogDetails = (props: CatalogDetailsProps) => {
  const getCatalog = async () => {
    props.setCatalogLoading(true);
    try {
      const catalogs = await listCatalogs({
        Filter: {
          CatalogKeyList: [
            {
              CatalogId: props.activeWorkspace.accountId,
              BucketName: props.bucketName,
              Region: props.activeWorkspace.region,
              DataSourceId: props.dataSource,
            },
          ],
        },
      });
      if (catalogs !== undefined && catalogs.CatalogInfoList !== undefined && catalogs.CatalogInfoList.length > 0) {
        let catalog = catalogs.CatalogInfoList[0];
        props.setGetCatalogSuccess(true);
        if (FOUNDRY_TRUSTED_ROLES.includes(catalog.DataAccessRole)) {
          props.setIsDefaultCatalog(true);
        } else if (
          catalog.DataAccessRole == getFoundryRoleARN(props.activeWorkspace.region, props.activeWorkspace.accountId)
        ) {
          props.setCTI(catalog?.CTI);
          props.setDescription(catalog?.Description);
          props.setCatalogName(catalog?.Name);
          props.setGetCatalogSuccess(true);
        } else {
          props.setIsGroupBasedCatalog(true);
        }
      } else {
        props.setGetCatalogSuccess(false);
        props.setCatalogName('');
        props.setDescription('');
        props.setCTI('');
      }
    } catch (err) {
      console.log('Exception when get catalog', err);
      props.setGetCatalogSuccess(false);
      props.setCatalogName('');
      props.setDescription('');
      props.setCTI('');
    }
    props.setCatalogLoading(false);
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    props.setCatalogLoading(true);
    const timeoutId = setTimeout(() => {
      handleRefresh();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [props.bucketName, props.dataSource]);

  const handleRefresh = () => {
    getCatalog();
  };

  return (
    <div>
      <br />
      <Form>
        <Container header={<Header variant={'h2'}>Catalog details</Header>}>
          <ColumnLayout>
            <FormField label={<div>Account ID</div>} description='The AWS account for the Glue catalog.'>
              <Input
                placeholder='Input the account ID here'
                ariaRequired={true}
                value={props.activeWorkspace.accountId}
                disabled
              />
            </FormField>

            <FormField label={<div>Catalog owner</div>} description='Catalog owner group or workspace ID.'>
              <Input
                placeholder='Input the group or workspace ID here'
                ariaRequired={true}
                value={props.activeWorkspace.workspaceName}
                disabled
              />
            </FormField>

            <FormField label={<div>Region</div>} description='The AWS region of the catalog.'>
              <Input ariaRequired={true} value={getRegion()} disabled />
            </FormField>

            <FormField label={'Catalog type'} description={'Leave as default unless you are using S3 Tables catalogs.'}>
              <RadioGroup
                value={props.dataSource}
                onChange={(e) => {
                  props.setDataSource(e.detail.value);
                  if (e.detail.value == 'glueLF') {
                    props.setBucketName(undefined);
                  }
                }}
                items={[
                  { value: 'glueLF', label: 'Default Glue catalog' },
                  { value: 's3table', label: 'S3 Tables catalog' },
                ]}
              />
            </FormField>

            {props.dataSource == 's3table' && (
              <FormField label={'S3 table bucket name'} description={'The name of the S3 table bucket to onboard.'}>
                <Input
                  ariaRequired={true}
                  value={props.bucketName}
                  onChange={(e) => props.setBucketName(e.detail.value)}
                />
              </FormField>
            )}

            {props.catalogLoading && <Spinner />}

            {!props.catalogLoading && (
              <FormField label={<div>Catalog name</div>} description='Input the name for your catalog.'>
                {props.getCatalogSuccess && !props.isDefaultCatalog && !props.isGroupBasedCatalog ? (
                  <Input placeholder='Enter catalog name here' ariaRequired={true} value={props.catalogName} disabled />
                ) : (
                  <Input
                    placeholder='Enter catalog name here'
                    ariaRequired={true}
                    onChange={(event) => props.setCatalogName(event.detail.value)}
                    value={props.catalogName}
                  />
                )}
              </FormField>
            )}

            {!props.catalogLoading && (
              <FormField label={<div>Catalog description</div>} description='Input the description for your catalog.'>
                {props.getCatalogSuccess && !props.isDefaultCatalog && !props.isGroupBasedCatalog ? (
                  <Input placeholder='Enter description here' ariaRequired={true} value={props.description} disabled />
                ) : (
                  <Input
                    placeholder='Enter description here'
                    ariaRequired={true}
                    onChange={(event) => props.setDescription(event.detail.value)}
                    value={props.description}
                  />
                )}
              </FormField>
            )}

            {!props.catalogLoading && (
              <FormField label={<div>CTI</div>} description='Input a CTI for your catalog.'>
                {props.getCatalogSuccess && !props.isDefaultCatalog && !props.isGroupBasedCatalog ? (
                  <Input
                    placeholder='Enter CTI here. Format: Category/Type/Item'
                    ariaRequired={true}
                    value={props.CTI}
                    disabled
                  />
                ) : (
                  <Input
                    placeholder='Enter CTI here. Format: Category/Type/Item'
                    ariaRequired={true}
                    onChange={(event) => props.setCTI(event.detail.value)}
                    value={props.CTI}
                  />
                )}
              </FormField>
            )}
          </ColumnLayout>
        </Container>
      </Form>
    </div>
  );
};
